import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';
import TableContainer from '@mui/material/TableContainer';
import { useSession } from 'next-auth/react';
import {
  KitGridContainer,
  KitGridItem,
  KitButton,
  KitTable,
} from 'components/common/cms-kit';

// local imports
import PleaseWait from 'components/common/PleaseWait';
import { logClientException } from 'appinsights/clientAppInsights';
import { isAdmin, ROLE_CONST } from 'services/roleUtils';
import { callApi } from 'services/apiWrapper';
import NotAuthorized from 'components/common/NotAuthorized';
import CustomFilter from '../../../common/subcomponents/CustomFilter';
import SortableTableHeader from '../../../common/subcomponents/SortableTableHeader';
import FeatureSwitchCard from './FeatureSwitchCard';
import AddFeatureSwitchDialog from './AddFeatureSwitchDialog';
import useLoadingOrSigningOut from '../../../../services/hooks/useLoadingOrSigningOut';
import {
  FeatureSwitch,
  FeatureSwitchEnum,
} from '../../../../types/FetureSwitchTypes';
import { useInstituionsWithSubs } from './FeatureSwitchHooks';
import { InstitutionWithSubscription } from 'types/InstitutionTypes';
import {
  getSessionStoreString,
  setSessionStoreString,
} from 'services/storageUtils';

interface PageState {
  allRecords: FeatureSwitch[];
  tableRecords: React.ReactNode[][];
  filter: string;
  fetchedRecords: boolean;
  showAllRecords: boolean;
  sortDirection: 'asc' | 'desc';
  sortBy: string;
}

interface PaginationOptions {
  searching: boolean;
  page: number;
  perPage: number;
  totalRecords: number;
}

interface AddEditState {
  open: boolean;
  action: 'Add' | 'Update';
  editFeatureSwitch: FeatureSwitch | null;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  },
);

const PAGE_SIZE_NAME = 'fsl-ps';

const defaultErrorMessage =
  'An unexpected error has occurred. Please try again.';

const AdminFeatureSwitchList: React.FC = () => {
  const { data: session, status } = useSession();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addEditState, setAddEditState] = useState<AddEditState>({
    open: false,
    action: 'Add',
    editFeatureSwitch: null,
  });
  const [subList, setSubList] = useState<Array<InstitutionWithSubscription>>(
    [],
  );
  const [haveSubList, setHaveSubList] = useState<boolean>(false);
  const fetching = useRef<number>(0);

  const {
    data,
    error,
    isFetching: isFetchingInstutions,
  } = useInstituionsWithSubs(session ? true : false);

  const [pageState, setPageState] = useState<PageState>({
    allRecords: [],
    tableRecords: [],
    filter: '',
    fetchedRecords: false,
    showAllRecords: false,
    sortDirection: 'asc',
    sortBy: '',
  });

  const [paginationOptions, setPaginationOptions] = useState<PaginationOptions>(
    {
      searching: false,
      page: 0,
      perPage: 10,
      totalRecords: 0,
    },
  );

  const sortOptions = useRef<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'switch_name',
    direction: 'ASC',
  });

  const filterOptions = useRef<Record<string, string> | null>(null);

  const formatInstitutionSub = (value: string): string => {
    if (!value) return '';
    const subs = value.split(',');
    return subs
      .map((sub) => {
        const institution = subList.find((s) => s.subscriptionId === sub);
        return institution
          ? `${institution.name} (${institution.institutionType})`
          : sub;
      })
      .join(', ');
  };

  const showEdit = (code: FeatureSwitch) => {
    setAddEditState({ open: true, action: 'Update', editFeatureSwitch: code });
  };

  const showAdd = () => {
    setAddEditState({ open: true, action: 'Add', editFeatureSwitch: null });
  };

  const fetchFeatureSwitches = async (
    perPage = 10,
    page = 0,
    append = false,
  ) => {
    try {
      fetching.current += 1;
      setPaginationOptions((prev) => ({ ...prev, searching: true }));
      let path = `/api/db/feature-switch-list?page_size=${perPage}&page_number=${page}`;
      const qs: string[] = [];

      if (filterOptions.current) {
        for (const key in filterOptions.current) {
          if (filterOptions.current.hasOwnProperty(key)) {
            qs.push(`filter.${key}=${filterOptions.current[key]}`);
          }
        }
      }
      qs.push(
        `order_by=${sortOptions.current.field}:${sortOptions.current.direction}`,
      );

      if (qs.length > 0) {
        path += `&${qs.join('&')}`;
      }
      const res = await callApi(path);

      const tableRecords = res.results.map((r: FeatureSwitch) => [
        r.switch_name,
        r.switch_type,
        <Checkbox key={r.switch_name} checked={r.enabled} disabled />,
        <div
          key={r.switch_name}
          style={{
            maxWidth: '300px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {r.switch_type === FeatureSwitchEnum.Subscription
            ? formatInstitutionSub(r.value)
            : r.value}
        </div>,
        <KitButton
          key={r.switch_name}
          color="info"
          round
          justIcon
          size="sm"
          onClick={() => showEdit(r)}
          title="Edit code"
        >
          <EditIcon />
        </KitButton>,
      ]);

      let allRecords = [...pageState.allRecords];
      if (append) {
        res.results.forEach((r: FeatureSwitch) => {
          allRecords.push(r);
        });
      } else {
        allRecords = res.results;
      }
      fetching.current -= 1;
      setPageState((prev) => ({
        ...prev,
        allRecords,
        tableRecords,
      }));
      setPaginationOptions((prev) => ({
        ...prev,
        totalRecords: res.totalRecords,
        perPage,
        page,
        searching: false,
      }));
    } catch (error) {
      console.error(error);
      logClientException(error);
      fetching.current -= 1;
      setErrorMessage(defaultErrorMessage);
    }
  };

  const init = async () => {
    const storedPageSizeString = getSessionStoreString(PAGE_SIZE_NAME) ?? '10';
    const storedPageSize = Number.parseInt(storedPageSizeString, 10);
    await fetchFeatureSwitches(storedPageSize, 0);
  };

  const isWaiting = useLoadingOrSigningOut(status);

  useEffect(() => {
    if (data && !error) {
      const institutionsWithSubs = data.districts
        .map((inst) => inst)
        .concat(data.schools.map((inst) => inst))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        );
      setSubList(institutionsWithSubs);
      setHaveSubList(true);
    }
    if (error) {
      console.error(error);
      logClientException(error);
      setErrorMessage(defaultErrorMessage);
    }
  }, [data, error]);

  useEffect(() => {
    if (isWaiting) return;
    if (isAdmin(session) && haveSubList) {
      init();
    }
  }, [session, isWaiting, haveSubList]);

  if (isWaiting || isFetchingInstutions) {
    return <PleaseWait isLoading={true} />;
  }

  if (!isAdmin(session)) {
    return <NotAuthorized />;
  }

  const handleAddEditClose = async (doSave: boolean) => {
    setAddEditState((prev) => ({ ...prev, open: false }));
    if (doSave) {
      await fetchFeatureSwitches();
    }
  };

  const handleAlertClose = (
    _event: Event | React.SyntheticEvent<any, Event>,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage(null);
  };

  const filterOnChange = async (data: Record<string, string>) => {
    filterOptions.current = data;
    await fetchFeatureSwitches(
      paginationOptions.perPage,
      paginationOptions.page,
    );
  };

  const onSortChange = async (field: string, direction?: 'ASC' | 'DESC') => {
    if (sortOptions.current.field === field) {
      sortOptions.current.direction =
        sortOptions.current.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      sortOptions.current.field = field;
      sortOptions.current.direction = direction || 'ASC';
    }
    await fetchFeatureSwitches(
      paginationOptions.perPage,
      paginationOptions.page,
    );
  };

  const getSortDirection = (field: string): 'ASC' | 'DESC' | null => {
    return sortOptions.current.field === field
      ? sortOptions.current.direction
      : null;
  };

  const headerCols = [
    <SortableTableHeader
      key="switch_name"
      field="switch_name"
      text="Switch Name"
      sortDirection={getSortDirection('switch_name')}
      onSortChange={onSortChange}
    />,
    <SortableTableHeader
      key="switch_type"
      field="switch_type"
      text="Type"
      sortDirection={getSortDirection('switch_type')}
      onSortChange={onSortChange}
    />,
    <SortableTableHeader
      key="enabled"
      field="enabled"
      text="Enabled"
      sortDirection={getSortDirection('enabled')}
      onSortChange={onSortChange}
    />,
    <SortableTableHeader
      key="value"
      field="value"
      text="Value"
      sortDirection={getSortDirection('value')}
      onSortChange={onSortChange}
    />,
    '',
  ];

  const customFilterFields = () => {
    const fields: { name: string; label: string }[] = [];
    const user = session?.user;
    if (user?.role === ROLE_CONST.BoysTownAdmin) {
      fields.push({ name: 'switch_name', label: 'Switch Name' });
    }
    fields.push({ name: 'switch_type', label: 'Type' });
    return fields;
  };

  return (
    <React.Fragment>
      <PleaseWait isLoading={fetching.current > 0 || isFetchingInstutions} />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        <Alert
          onClose={handleAlertClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <CustomFilter fields={customFilterFields()} onChange={filterOnChange} />
        {pageState.allRecords.map((r) => {
          return (
            <FeatureSwitchCard
              key={r.id}
              featureSwitch={r}
              subscriptionValue={
                r.switch_type === FeatureSwitchEnum.Subscription
                  ? formatInstitutionSub(r.value)
                  : ''
              }
              editFunc={() => showEdit(r)}
            />
          );
        })}
        {paginationOptions.totalRecords >
          (paginationOptions.page + 1) * paginationOptions.perPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <KitButton
              size="sm"
              round
              color="secondary"
              onClick={() =>
                fetchFeatureSwitches(
                  paginationOptions.perPage,
                  paginationOptions.page + 1,
                  true,
                )
              }
            >
              Load More
            </KitButton>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: { sm: 'block', xs: 'none' },
          marginLeft: '15px',
          marginRight: '15px',
          marginBottom: '15px',
        }}
      >
        <CustomFilter fields={customFilterFields()} onChange={filterOnChange} />
        <KitGridContainer direction="row" align="right">
          <KitGridItem xs={12}>
            <KitButton size="sm" round color="primary" onClick={showAdd}>
              <AddCircleIcon /> Add Feature Switch
            </KitButton>
          </KitGridItem>
        </KitGridContainer>
        <KitGridContainer direction="row" align="center">
          <KitGridItem xs={12}>
            <TableContainer>
              <KitTable
                striped
                hover
                tableData={pageState.tableRecords}
                tableHead={headerCols}
              ></KitTable>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={paginationOptions.totalRecords}
                rowsPerPage={paginationOptions.perPage}
                page={paginationOptions.page}
                onPageChange={(ev, val) => {
                  fetchFeatureSwitches(paginationOptions.perPage, val);
                }}
                onRowsPerPageChange={(ev) => {
                  const perPage = parseInt(ev.target.value, 10);
                  setSessionStoreString(PAGE_SIZE_NAME, perPage);
                  fetchFeatureSwitches(perPage, 0);
                }}
                showFirstButton
                showLastButton
              />
            </TableContainer>
          </KitGridItem>
        </KitGridContainer>
      </Box>

      <AddFeatureSwitchDialog
        open={addEditState.open}
        onClose={handleAddEditClose}
        action={addEditState.action}
        user={session?.user || {}}
        subList={subList}
        editFeatureSwitch={addEditState.editFeatureSwitch ?? undefined}
      />
    </React.Fragment>
  );
};

export default AdminFeatureSwitchList;
